<!-- NOTICE: KAPTION-INTEGRATION -->

<script>
/* eslint-disable no-console */
import { toRef, computed } from 'vue';
import { MESSAGE_TYPE } from 'shared/constants/messages';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
// NOTICE: KAPTION-INTEGRATION - feature-permissions.AI.1 / CORE.5: gate the panel
// on the same resolver the backend authorize_feature! uses.
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';
import { useKaptionAIFeaturesPreferences } from 'dashboard/composables/useKaptionAIFeaturesPreferences';
import { useUISettings } from 'dashboard/composables/useUISettings';
import kaptionAiSuggestionsAPI from 'dashboard/api/kaptionAiSuggestions';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
// NOTICE: KAPTION-INTEGRATION - Import MessageFormatter for markdown rendering
import MessageFormatter from 'shared/helpers/MessageFormatter.js';
// NOTICE: KAPTION-INTEGRATION - usage-analytics.SUGGESTIONS.1
import { trackUsage, safeUuid } from 'dashboard/composables/useUsageAnalytics';
import { USAGE_ANALYTICS_EVENTS } from 'dashboard/helper/usageAnalytics/constants';
// NOTICE: KAPTION-INTEGRATION - Import Knowledge Base Modal
import KaptionKnowledgeBaseModal from './KaptionKnowledgeBaseModal.vue';

// NOTICE: KAPTION-INTEGRATION - suggestion polling backoff schedule (ms). While a
// suggestion pre-calculates, Studio Chat answers "calculating"; we retry on this
// bounded schedule (8 tries ≈ 21s total) and then stop until the next inbound
// message re-triggers a fresh fetch. Starts snappy (~0.6s) so a suggestion that
// finishes quickly shows almost immediately, then eases off so a slow one still
// surfaces — without ever becoming a tight/continuous poll. The backend releases
// its DB connection around each upstream call, so extra tries don't tie up the AR
// pool (see kaption_ai_suggestion_service.rb#fetch_real_suggestions).
const SUGGESTION_POLL_BACKOFF_MS = [
  600, 900, 1300, 1800, 2500, 3500, 4500, 6000,
];

export default {
  name: 'KaptionChatSuggestions',
  components: {
    KaptionKnowledgeBaseModal,
  },
  props: {
    conversationId: {
      type: [String, Number],
      required: true,
    },
    inboxId: {
      type: [String, Number],
      required: true,
    },
    accountId: {
      type: [String, Number],
      required: true,
    },
  },
  emits: ['suggestionSelected'],
  setup(props) {
    // NOTICE: KAPTION-INTEGRATION - Two-gate system: feature flag + user preference
    const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
    // NOTICE: KAPTION-INTEGRATION - feature-permissions.AI.1: the account/user
    // permission for ai_reply_suggestions (matches the backend authorize_feature!).
    const { canUseFeature } = useFeaturePermissions();
    // Use toRef to create reactive reference from prop to ensure preferences reload when switching conversations
    const conversationIdRef = toRef(props, 'conversationId');
    const { suggestionsEnabled, setSuggestions } =
      useKaptionAIFeaturesPreferences(conversationIdRef);

    // NOTICE: KAPTION-INTEGRATION - Get master toggle from uiSettings (topbar setting)
    const { uiSettings } = useUISettings();
    const masterToggleEnabled = computed(
      () => uiSettings.value?.kaption_ai_suggestions_enabled ?? true
    );

    // NOTICE: KAPTION-INTEGRATION - Combined check: the feature must be permitted
    // (feature-permissions.AI.1 — else the panel renders, fetches, and the backend
    // refuses with feature_not_permitted) AND master toggle AND per-conversation pref.
    const shouldShowSuggestions = computed(
      () =>
        canUseFeature('ai_reply_suggestions') &&
        masterToggleEnabled.value &&
        suggestionsEnabled.value
    );

    return {
      isKaptionFlagEnabled,
      suggestionsEnabled,
      masterToggleEnabled,
      shouldShowSuggestions,
      // NOTICE: KAPTION-INTEGRATION - Store setSuggestions as method, not in reactive state
      // This prevents Histoire state preservation errors when cloning component state
      setSuggestions: enabled => {
        try {
          setSuggestions(enabled);
        } catch (err) {
          console.error(
            '[KaptionChatSuggestions] Error setting suggestions:',
            err
          );
        }
      },
    };
  },
  data() {
    return {
      suggestion: null,
      isLoading: false,
      error: null,
      // NOTICE: KAPTION-INTEGRATION - feature-permissions.CORE.6: set if the backend
      // ever refuses with feature_not_permitted (fail-open map race); hides the panel
      // instead of leaking the raw error code.
      featureBlocked: false,
      refreshTimeout: null,
      isCollapsing: false,
      scrollTop: 0,
      scrollHeight: 0,
      clientHeight: 0,
      // NOTICE: KAPTION-INTEGRATION - Modal state for knowledge base details
      showKnowledgeBaseModal: false,
      // NOTICE: KAPTION-INTEGRATION - Store metadata separately
      metadata: null,
      // NOTICE: KAPTION-INTEGRATION - usage-analytics.SUGGESTIONS.1
      // Client-minted id for the currently-rendered suggestion + the epoch ms it
      // was shown, so we can compute time_to_decision on accept/cancel and thread
      // the id up to ReplyBox on accept.
      usageSuggestionId: null,
      usageSuggestionShownAt: null,
      // NOTICE: KAPTION-INTEGRATION - bounded backoff polling while a suggestion
      // pre-calculates (see SUGGESTION_POLL_BACKOFF_MS).
      pollAttempts: 0,
      pollTimeout: null,
      // NOTICE: KAPTION-INTEGRATION - usage-analytics.SUGGESTIONS.4 - 'up' | 'down'
      // once the agent votes on the current suggestion; null = not voted yet.
      feedbackGiven: null,
      // Glyphs held in data (not inline in the template) to satisfy the i18n
      // no-raw-text lint rule, same pattern as confidenceIcon.
      thumbUpIcon: '👍',
      thumbDownIcon: '👎',
    };
  },
  computed: {
    // NOTICE: KAPTION-INTEGRATION - A suggestion is only useful while the
    // customer's message is the latest word in the conversation. Once the
    // agent (or a template/campaign send) has replied, fetching would only
    // poll for a suggestion that no longer applies — and on stale
    // conversations it dead-ends in the full not_ready poll cycle with a
    // spinner. Walk the timeline backwards past private notes/activities.
    conversationNeedsReply() {
      const conversation = this.$store.getters.getConversationById(
        Number(this.conversationId)
      );
      const messages = conversation?.messages || [];
      for (let i = messages.length - 1; i >= 0; i -= 1) {
        const message = messages[i];
        if (!message.private) {
          if (message.message_type === MESSAGE_TYPE.INCOMING) return true;
          if (
            message.message_type === MESSAGE_TYPE.OUTGOING ||
            message.message_type === MESSAGE_TYPE.TEMPLATE
          )
            return false;
        }
      }
      // No customer message in the loaded timeline: nothing to suggest on.
      return false;
    },
    // NOTICE: KAPTION-INTEGRATION - Get knowledge bases from metadata
    knowledgeBases() {
      if (this.metadata?.knowledgebases_used) {
        return this.metadata.knowledgebases_used;
      }
      // Fallback to suggestion's knowledgebases_used if metadata is not available
      if (this.suggestion?.knowledgebases_used) {
        return this.suggestion.knowledgebases_used;
      }
      return [];
    },
    confidenceClass() {
      if (!this.suggestion) return '';
      const baseClasses =
        'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold';
      const stateClasses = {
        HIGH: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
        MEDIUM:
          'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
        LOW: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      };
      return `${baseClasses} ${
        stateClasses[this.suggestion.confidence] || stateClasses.MEDIUM
      }`;
    },
    confidenceIcon() {
      if (!this.suggestion) return '?';
      const icons = {
        HIGH: '✓',
        MEDIUM: '~',
        LOW: '!',
      };
      return icons[this.suggestion.confidence] || '?';
    },
    showBottomGradient() {
      return (
        this.scrollHeight > this.clientHeight &&
        this.scrollTop < this.scrollHeight - this.clientHeight - 5
      );
    },
    showTopGradient() {
      // Show top gradient when scrolled down from the top
      return this.scrollHeight > this.clientHeight && this.scrollTop > 5;
    },
    showScrollIndicator() {
      // Only show arrow when at top (or very close to top) and there's more content below
      return this.scrollHeight > this.clientHeight && this.scrollTop < 10;
    },
    // NOTICE: KAPTION-INTEGRATION - Format suggestion text as markdown
    formattedSuggestionText() {
      if (!this.suggestion?.text) return '';
      return new MessageFormatter(this.suggestion.text).formattedMessage;
    },
  },
  watch: {
    conversationId() {
      // Clear old suggestion immediately when switching conversations
      // NOTICE: KAPTION-INTEGRATION - usage-analytics.SUGGESTIONS.3
      this.cancelSuggestion('conversation_switch');
      this.suggestion = null;
      this.metadata = null;
      this.error = null;
      this.fetchSuggestion();
    },
    inboxId() {
      // Clear old suggestion immediately when switching inboxes
      // NOTICE: KAPTION-INTEGRATION - usage-analytics.SUGGESTIONS.3
      this.cancelSuggestion('inbox_switch');
      this.suggestion = null;
      this.metadata = null;
      this.error = null;
      this.fetchSuggestion();
    },
    // NOTICE: KAPTION-INTEGRATION - Watch user preference toggle
    suggestionsEnabled(newValue) {
      if (!newValue) {
        // Clear suggestion when disabled
        // NOTICE: KAPTION-INTEGRATION - usage-analytics.SUGGESTIONS.3
        this.cancelSuggestion('toggled_off');
        this.suggestion = null;
        this.metadata = null;
        this.error = null;
      } else if (this.masterToggleEnabled) {
        // Fetch suggestion when enabled (only if master toggle is also on)
        this.fetchSuggestion();
      }
    },
    // NOTICE: KAPTION-INTEGRATION - Watch master toggle from topbar settings
    masterToggleEnabled(newValue) {
      if (!newValue) {
        // Clear suggestion when master toggle is disabled
        // NOTICE: KAPTION-INTEGRATION - usage-analytics.SUGGESTIONS.3
        this.cancelSuggestion('toggled_off');
        this.suggestion = null;
        this.metadata = null;
        this.error = null;
      } else if (this.suggestionsEnabled) {
        // Fetch suggestion when master toggle is enabled (only if per-conversation is also on)
        this.fetchSuggestion();
      }
    },
    suggestion() {
      // Update scroll state when suggestion changes
      this.updateScrollState();
    },
    // NOTICE: KAPTION-INTEGRATION - The moment the agent answers, the shown
    // suggestion is obsolete: close it out (usage: canceled/agent_replied),
    // clear the panel, and stop any in-flight polling.
    conversationNeedsReply(newValue) {
      if (newValue) return;
      if (this.usageSuggestionId) {
        this.cancelSuggestion('agent_replied');
      }
      this.suggestion = null;
      this.metadata = null;
      this.error = null;
      this.isLoading = false;
      this.resetPolling();
    },
  },
  created() {
    emitter.on(BUS_EVENTS.ADD_MESSAGE, this.handleNewMessage);
  },
  mounted() {
    this.fetchSuggestion();
    // NOTICE: KAPTION-INTEGRATION - Listen for suggestions toggle events
    emitter.on('kaption-suggestions-toggled', this.handleSuggestionsToggle);
  },
  unmounted() {
    emitter.off(BUS_EVENTS.ADD_MESSAGE, this.handleNewMessage);
    // NOTICE: KAPTION-INTEGRATION - Remove suggestions toggle event listener
    emitter.off('kaption-suggestions-toggled', this.handleSuggestionsToggle);
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
    }
    // NOTICE: KAPTION-INTEGRATION - cancel any pending suggestion poll.
    this.clearPollTimer();
  },
  methods: {
    async fetchSuggestion({ isPoll = false } = {}) {
      if (!this.conversationId || !this.inboxId || !this.accountId) {
        return;
      }

      // NOTICE: KAPTION-INTEGRATION - Check both master toggle AND user preference
      if (!this.shouldShowSuggestions) {
        return;
      }

      // NOTICE: KAPTION-INTEGRATION - Skip entirely once the agent has already
      // replied: no fetch, no poll cycle, no spinner.
      if (!this.conversationNeedsReply) {
        this.resetPolling();
        return;
      }

      // NOTICE: KAPTION-INTEGRATION - a fresh (non-poll) fetch starts a new poll
      // cycle; clear any in-flight backoff so counters don't leak across messages.
      if (!isPoll) {
        this.resetPolling();
      }

      // NOTICE: KAPTION-INTEGRATION - usage-analytics.SUGGESTIONS.3
      // If a suggestion is currently shown and we're about to (re)fetch (e.g. a
      // new message arrived), close out the prior one as canceled before a fresh
      // suggestion_shown is minted. No-op when nothing is currently shown.
      if (this.usageSuggestionId) {
        this.cancelSuggestion('new_message');
      }

      this.isLoading = true;
      this.error = null;
      // NOTICE: KAPTION-INTEGRATION - usage-analytics.SUGGESTIONS.1 (latency_to_show)
      const usageFetchStartedAt = Date.now();

      try {
        const response = await kaptionAiSuggestionsAPI.fetchSuggestions({
          conversationId: this.conversationId,
          inboxId: this.inboxId,
        });

        // NOTICE: KAPTION-INTEGRATION - Ensure data is serializable (no functions)
        // This prevents Histoire state preservation errors when cloning component state
        const suggestionData = response.data.suggestions?.[0];
        const status = response.data.status;
        if (suggestionData) {
          // Create a clean copy without any function references
          this.suggestion = {
            text: suggestionData.text,
            confidence: suggestionData.confidence,
            explanation: suggestionData.explanation,
            knowledgebases_used: suggestionData.knowledgebases_used
              ? JSON.parse(JSON.stringify(suggestionData.knowledgebases_used))
              : [],
          };
        } else if (status === 'calculating' || status === 'not_ready') {
          // NOTICE: KAPTION-INTEGRATION - still pre-calculating in the background;
          // keep the panel as-is (nothing shown) and poll again with backoff.
          this.schedulePoll();
          return;
        } else {
          this.suggestion = null;
        }

        // NOTICE: KAPTION-INTEGRATION - reached a terminal state (a suggestion is
        // shown, or the result is genuinely empty); stop any pending poll.
        this.resetPolling();

        // Store metadata (ensure it's serializable)
        const metadataData = response.data.metadata;
        if (metadataData) {
          this.metadata = JSON.parse(JSON.stringify(metadataData));
        } else {
          this.metadata = null;
        }

        // Update scroll state after suggestion is set
        this.updateScrollState();

        // NOTICE: KAPTION-INTEGRATION - Tell MessagesView to follow the bottom.
        // When the panel grows to fit a fresh suggestion the messages container
        // shrinks; without this nudge the last messages slip behind the panel
        // (BFG/Marcela). Only emit when a suggestion was actually rendered.
        if (this.suggestion) {
          emitter.emit('kaption-suggestion-arrived');
          // NOTICE: KAPTION-INTEGRATION - usage-analytics.SUGGESTIONS.1
          this.trackSuggestionShown(
            response.data,
            suggestionData,
            usageFetchStartedAt
          );
        }
      } catch (err) {
        // NOTICE: KAPTION-INTEGRATION - Better error handling
        console.error(
          '[KaptionChatSuggestions] Error fetching suggestion:',
          err
        );
        this.suggestion = null;
        this.metadata = null;
        // NOTICE: KAPTION-INTEGRATION - feature-permissions.CORE.6: never surface the
        // raw feature_not_permitted code. If the feature is refused (the panel slipped
        // through the fail-open permission race), hide it silently — no error, no Retry
        // that would re-call the refused endpoint. Otherwise show a friendly message.
        if (
          err.isFeatureNotPermitted ||
          err.response?.data?.error === 'feature_not_permitted'
        ) {
          this.featureBlocked = true;
          this.error = null;
        } else {
          this.error = this.$t('AI_SUGGESTIONS.ERROR_MESSAGE');
        }
      } finally {
        // NOTICE: KAPTION-INTEGRATION - keep the spinner up while a background
        // poll is pending; only clear it once we reach a terminal state.
        if (!this.pollTimeout) {
          this.isLoading = false;
        }
      }
    },
    handleNewMessage(message) {
      if (message.conversation_id === this.conversationId) {
        if (this.refreshTimeout) clearTimeout(this.refreshTimeout);
        this.refreshTimeout = setTimeout(() => {
          this.fetchSuggestion();
        }, 100);
      }
    },
    onSuggestionSelect() {
      if (!this.suggestion?.text) return;

      // NOTICE: KAPTION-INTEGRATION - usage-analytics.SUGGESTIONS.2
      // Emit accepted BEFORE clearing state so suggestion_id / timing are known,
      // and thread the suggestion_id + model up to ReplyBox so it can correlate a
      // subsequent send (suggestion_sent). Payload is an object; ReplyBox handles
      // both the object and the legacy string form.
      const usageSuggestionId = this.usageSuggestionId;
      const model = this.metadata?.model ?? null;
      this.trackSuggestionAccepted(usageSuggestionId, model);

      // Emit the selected suggestion text (+ analytics correlation metadata)
      this.$emit('suggestionSelected', {
        text: this.suggestion.text,
        suggestionId: usageSuggestionId,
        model,
      });

      // The suggestion has been consumed by an accept; clear the id so the
      // deferred collapse doesn't also fire a cancel for it.
      this.usageSuggestionId = null;
      this.usageSuggestionShownAt = null;

      // Trigger collapse animation
      this.isCollapsing = true;

      // Clear suggestion after animation completes (200ms)
      setTimeout(() => {
        this.suggestion = null;
        this.isCollapsing = false;
      }, 200);
    },
    // NOTICE: KAPTION-INTEGRATION - usage-analytics.SUGGESTIONS.1
    // Mint a fresh client suggestion_id whenever a suggestion renders and emit
    // suggestion_shown. Metadata-only (no suggestion text). Best-effort.
    trackSuggestionShown(responseData, suggestionData, startedAt) {
      try {
        this.usageSuggestionId = safeUuid();
        this.usageSuggestionShownAt = Date.now();
        // NOTICE: KAPTION-INTEGRATION - a fresh suggestion resets the 👍/👎 vote.
        this.feedbackGiven = null;
        const metadata = responseData?.metadata || this.metadata || {};
        const kbUsed =
          metadata.knowledgebases_used ||
          suggestionData?.knowledgebases_used ||
          [];
        trackUsage(USAGE_ANALYTICS_EVENTS.SUGGESTION_SHOWN, {
          suggestion_id: this.usageSuggestionId,
          num_suggestions_shown: Array.isArray(responseData?.suggestions)
            ? responseData.suggestions.length
            : 1,
          position: 0,
          model: metadata.model ?? null,
          confidence: suggestionData?.confidence ?? null,
          knowledgebases_used_count: Array.isArray(kbUsed) ? kbUsed.length : 0,
          latency_to_show_ms: Date.now() - startedAt,
        });
      } catch (e) {
        // best-effort
      }
    },
    // NOTICE: KAPTION-INTEGRATION - usage-analytics.SUGGESTIONS.2
    trackSuggestionAccepted(suggestionId, model) {
      try {
        if (!suggestionId) return;
        trackUsage(USAGE_ANALYTICS_EVENTS.SUGGESTION_ACCEPTED, {
          suggestion_id: suggestionId,
          model: model ?? null,
          time_to_decision_ms: this.usageSuggestionShownAt
            ? Date.now() - this.usageSuggestionShownAt
            : null,
        });
      } catch (e) {
        // best-effort
      }
    },
    // NOTICE: KAPTION-INTEGRATION - usage-analytics.SUGGESTIONS.3
    // Emit suggestion_canceled for a shown-but-not-accepted suggestion. Guarded
    // so it only fires once per shown suggestion (id is cleared after). reason is
    // a bounded enum: 'closed' | 'new_message' | 'conversation_switch' |
    // 'inbox_switch' | 'toggled_off'.
    cancelSuggestion(reason) {
      try {
        if (!this.usageSuggestionId) return;
        trackUsage(USAGE_ANALYTICS_EVENTS.SUGGESTION_CANCELED, {
          suggestion_id: this.usageSuggestionId,
          reason,
          time_to_decision_ms: this.usageSuggestionShownAt
            ? Date.now() - this.usageSuggestionShownAt
            : null,
        });
      } catch (e) {
        // best-effort
      } finally {
        this.usageSuggestionId = null;
        this.usageSuggestionShownAt = null;
      }
    },
    // NOTICE: KAPTION-INTEGRATION - bounded backoff polling while a suggestion
    // pre-calculates. Caps at SUGGESTION_POLL_BACKOFF_MS.length tries, then stops
    // until the next inbound message re-triggers a fresh fetch. Kept intentionally
    // light: the backend releases its DB connection around the upstream call, and
    // we never poll in a tight loop.
    schedulePoll() {
      if (this.pollAttempts >= SUGGESTION_POLL_BACKOFF_MS.length) {
        this.resetPolling();
        return;
      }
      const delay = SUGGESTION_POLL_BACKOFF_MS[this.pollAttempts];
      this.pollAttempts += 1;
      this.clearPollTimer();
      this.pollTimeout = setTimeout(() => {
        this.pollTimeout = null;
        this.fetchSuggestion({ isPoll: true });
      }, delay);
    },
    resetPolling() {
      this.pollAttempts = 0;
      this.clearPollTimer();
    },
    clearPollTimer() {
      if (this.pollTimeout) {
        clearTimeout(this.pollTimeout);
        this.pollTimeout = null;
      }
    },
    // NOTICE: KAPTION-INTEGRATION - usage-analytics.SUGGESTIONS.4
    // 👍/👎 on the shown suggestion. Metadata-only, reuses the SAME client
    // suggestion_id as shown/accepted/canceled so the funnel can join quality
    // (verdict) to usage. One vote per suggestion; best-effort (never throws).
    trackSuggestionFeedback(verdict) {
      try {
        if (!this.usageSuggestionId || this.feedbackGiven) return;
        this.feedbackGiven = verdict;
        trackUsage(USAGE_ANALYTICS_EVENTS.SUGGESTION_FEEDBACK, {
          suggestion_id: this.usageSuggestionId,
          verdict,
          confidence: this.suggestion?.confidence ?? null,
          model: this.metadata?.model ?? null,
          time_to_decision_ms: this.usageSuggestionShownAt
            ? Date.now() - this.usageSuggestionShownAt
            : null,
        });
      } catch (e) {
        // best-effort
      }
    },
    async onCopyToClipboard() {
      if (this.suggestion?.text) {
        try {
          await navigator.clipboard.writeText(this.suggestion.text);
          this.$notify.success(this.$t('AI_SUGGESTIONS.COPIED_TO_CLIPBOARD'));
        } catch (err) {
          this.$notify.error(this.$t('AI_SUGGESTIONS.COPY_FAILED'));
        }
      }
    },
    onRetry() {
      this.fetchSuggestion();
    },
    // NOTICE: KAPTION-INTEGRATION - Handle suggestions toggle event from KaptionAIFeaturesPill
    handleSuggestionsToggle({ conversationId, enabled }) {
      if (conversationId === this.conversationId) {
        // Update the composable's ref to maintain reactivity
        this.setSuggestions(enabled);
        // The watcher on suggestionsEnabled will handle clearing/fetching
      }
    },
    onScroll(event) {
      const target = event.target;
      this.scrollTop = target.scrollTop;
      this.scrollHeight = target.scrollHeight;
      this.clientHeight = target.clientHeight;
    },
    updateScrollState() {
      // Update scroll state when suggestion changes
      this.$nextTick(() => {
        const scrollContainer = this.$refs.scrollContainer;
        if (scrollContainer) {
          this.scrollTop = scrollContainer.scrollTop;
          this.scrollHeight = scrollContainer.scrollHeight;
          this.clientHeight = scrollContainer.clientHeight;
        }
      });
    },
    // NOTICE: KAPTION-INTEGRATION - Close suggestion and clear it
    closeSuggestion() {
      // NOTICE: KAPTION-INTEGRATION - usage-analytics.SUGGESTIONS.3
      this.cancelSuggestion('closed');
      this.suggestion = null;
      this.metadata = null;
    },
    // NOTICE: KAPTION-INTEGRATION - Handle citation pill click (opens modal with all sources)
    onCitationPillClick(event) {
      event.stopPropagation(); // Prevent triggering suggestion select
      // Open modal with all knowledge bases - modal will handle source selection
      this.showKnowledgeBaseModal = true;
    },
    // NOTICE: KAPTION-INTEGRATION - Close knowledge base modal
    onCloseKnowledgeBaseModal() {
      this.showKnowledgeBaseModal = false;
    },
  },
};
</script>

<template>
  <div
    v-if="shouldShowSuggestions && !featureBlocked && conversationNeedsReply"
  >
    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center gap-2 py-2">
      <div
        class="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin dark:border-purple-400"
      />
      <span class="text-sm text-slate-600 dark:text-slate-400">
        {{ $t('AI_SUGGESTIONS.LOADING') }}
      </span>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="py-2">
      <div
        class="flex items-center justify-between bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-2"
      >
        <span class="text-sm text-red-800 dark:text-red-300">
          {{ error }}
        </span>
        <button
          class="text-red-600 dark:text-red-400 hover:text-red-800 text-xs font-medium"
          @click="onRetry"
        >
          {{ $t('AI_SUGGESTIONS.RETRY') }}
        </button>
      </div>
    </div>

    <!-- Suggestion Card - Click to use -->
    <!-- NOTICE: KAPTION-INTEGRATION - Green background with green scroll gradient -->
    <!-- NOTICE: KAPTION-INTEGRATION - Capped outer height so the panel no longer
         pushes the reply-box up over the conversation thread (BFG/Marcela). -->
    <div
      v-else-if="suggestion || isCollapsing"
      class="cursor-pointer bg-green-50 dark:bg-green-900 border border-green-200/50 dark:border-green-800/50 rounded-lg overflow-hidden hover:border-green-400/50 dark:hover:border-green-600/50 hover:shadow-md active:shadow-lg active:scale-[0.99] transition-all duration-200 relative"
      :class="[
        {
          'opacity-0 -translate-y-2 max-h-0 py-0 mb-0': isCollapsing,
          'opacity-100 translate-y-0 max-h-56 p-3': !isCollapsing,
        },
      ]"
      @click="onSuggestionSelect"
    >
      <!-- NOTICE: KAPTION-INTEGRATION - Close button in top right -->
      <button
        class="absolute top-2 right-2 p-1 rounded hover:bg-green-200/50 dark:hover:bg-green-800/50 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors z-20"
        :title="$t('GENERAL.CLOSE')"
        @click.stop="closeSuggestion"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          class="w-4 h-4"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>

      <div class="flex gap-3">
        <!-- AI Icon -->
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          class="w-4 h-4 text-green-600/50 dark:text-green-300/50 flex-shrink-0 mt-0.5"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
          />
        </svg>

        <div class="flex-1 min-w-0 relative">
          <!-- Scrollable Suggestion Text Container -->
          <!-- NOTICE: KAPTION-INTEGRATION - Reduced inner scroll height so the
               recommendations panel doesn't occlude the chat (BFG/Marcela). -->
          <div
            ref="scrollContainer"
            class="max-h-32 overflow-y-auto pr-1 suggestion-scroll mb-2 relative"
            @scroll="onScroll"
          >
            <!-- Top Gradient Indicator - NOTICE: KAPTION-INTEGRATION - Gradient matches card background exactly (green-50/50 or green-900/50) -->
            <div
              v-if="showTopGradient"
              class="sticky top-0 left-0 right-0 h-12 pointer-events-none transition-opacity duration-200 bg-gradient-to-b from-green-50 to-transparent dark:from-green-900 dark:to-transparent z-10"
            />

            <!-- Suggestion Text - NOTICE: KAPTION-INTEGRATION - Rendered as markdown -->
            <span
              v-dompurify-html="formattedSuggestionText"
              class="text-sm text-slate-600 dark:text-slate-300 leading-relaxed break-words prose prose-bubble"
            />

            <!-- Bottom Gradient Indicator (only on text content) -->
            <!-- NOTICE: KAPTION-INTEGRATION - Gradient matches card background exactly (green-50/50 or green-900/50) -->
            <div
              v-if="showBottomGradient"
              class="sticky bottom-0 left-0 right-0 h-12 pointer-events-none transition-opacity duration-200 bg-gradient-to-t from-green-50 to-transparent dark:from-green-900 dark:to-transparent"
            />

            <!-- Scroll Indicator (small chevron down icon) -->
            <Transition name="fade">
              <div
                v-if="showScrollIndicator"
                class="absolute bottom-1 right-2 pointer-events-none z-10 scroll-indicator"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="2.5"
                  stroke="currentColor"
                  class="w-3.5 h-3.5 text-green-600/70 dark:text-green-400/70 animate-bounce"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M19.5 13.5L12 21m0 0l-7.5-7.5M12 21V3"
                  />
                </svg>
              </div>
            </Transition>
          </div>

          <!-- Metadata: Confidence Badge & Knowledge Bases (always visible) -->
          <div class="flex items-center gap-2 flex-wrap">
            <!-- Confidence Badge -->
            <span :class="confidenceClass">
              <span>{{ confidenceIcon }}</span>
              <span>{{ suggestion.confidence }}</span>
            </span>

            <!-- Knowledge Base Citation Pill - NOTICE: KAPTION-INTEGRATION - Single pill with icon and count -->
            <button
              v-if="knowledgeBases.length"
              class="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-woot-100/50 text-woot-800 dark:bg-woot-900/50 dark:text-woot-200 hover:bg-woot-200/50 dark:hover:bg-woot-800/50 transition-colors cursor-pointer"
              @click="onCitationPillClick($event)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="2"
                stroke="currentColor"
                class="w-3.5 h-3.5"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                />
              </svg>
              <span>{{ knowledgeBases.length }}</span>
            </button>

            <!-- NOTICE: KAPTION-INTEGRATION - usage-analytics.SUGGESTIONS.4 - 👍/👎
                 feedback. @click.stop so a vote never triggers the card's
                 "use suggestion" click. One vote per suggestion. -->
            <div class="flex items-center gap-1 ml-auto">
              <button
                type="button"
                :title="
                  feedbackGiven
                    ? $t('AI_SUGGESTIONS.FEEDBACK_THANKS')
                    : $t('AI_SUGGESTIONS.FEEDBACK_HELPFUL')
                "
                :disabled="feedbackGiven !== null"
                class="p-1 rounded transition-colors leading-none disabled:cursor-default"
                :class="
                  feedbackGiven === 'up'
                    ? 'bg-green-200/70 dark:bg-green-700/50'
                    : 'text-slate-500 dark:text-slate-400 hover:bg-green-200/50 dark:hover:bg-green-800/50'
                "
                @click.stop="trackSuggestionFeedback('up')"
              >
                <span class="text-sm leading-none">{{ thumbUpIcon }}</span>
              </button>
              <button
                type="button"
                :title="
                  feedbackGiven
                    ? $t('AI_SUGGESTIONS.FEEDBACK_THANKS')
                    : $t('AI_SUGGESTIONS.FEEDBACK_NOT_HELPFUL')
                "
                :disabled="feedbackGiven !== null"
                class="p-1 rounded transition-colors leading-none disabled:cursor-default"
                :class="
                  feedbackGiven === 'down'
                    ? 'bg-red-200/70 dark:bg-red-700/50'
                    : 'text-slate-500 dark:text-slate-400 hover:bg-red-200/50 dark:hover:bg-red-800/50'
                "
                @click.stop="trackSuggestionFeedback('down')"
              >
                <span class="text-sm leading-none">{{ thumbDownIcon }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- NOTICE: KAPTION-INTEGRATION - Knowledge Base Details Modal -->
    <KaptionKnowledgeBaseModal
      :show="showKnowledgeBaseModal"
      :knowledge-bases="knowledgeBases"
      @close="onCloseKnowledgeBaseModal"
    />
  </div>
</template>

<style scoped>
/* Custom scrollbar styling for suggestion content */
.suggestion-scroll::-webkit-scrollbar {
  width: 6px;
}

.suggestion-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.suggestion-scroll::-webkit-scrollbar-thumb {
  background: rgba(34, 197, 94, 0.3);
  border-radius: 3px;
}

.suggestion-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(34, 197, 94, 0.5);
}

/* Firefox scrollbar */
.suggestion-scroll {
  scrollbar-width: thin;
  scrollbar-color: rgba(34, 197, 94, 0.3) transparent;
}

/* Fade transition for scroll indicator */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}
</style>
